import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, KeyValue, Select, Spinner, Textarea } from '@zt/ui';
import { api, ApiError, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { PrivacyRequestDetail, PrivacyRequestRow, Store } from '../lib/types';

const KINDS = ['export', 'erasure', 'info', 'rectification', 'objection', 'other'] as const;
const CHANNELS = ['email', 'kep', 'post', 'in_person', 'other'] as const;
const statusTone = { received: 'amber', in_progress: 'pine', completed: 'moss', rejected: 'slate' } as const;

/**
 * KVKK ilgili kişi başvuruları (F3B-17): 30 günlük süre takibi, verilerin kopyası, silme başvurusunda
 * etki önizlemesi ve iki aşamalı onayla anonimleştirme.
 */
export function PrivacyPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [state, setState] = useState<'open' | 'closed'>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = useQuery({
    queryKey: ['privacy', state],
    queryFn: () => api.get<PrivacyRequestRow[]>(`/v1/admin/privacy/requests?state=${state}`),
  });
  return (
    <PageFrame
      helpKey="page.privacy"
      actions={
        can('privacy.request.manage') && (
          <span className="flex items-center gap-1">
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.privacy.create')}
            </Button>
            <HelpTip k="privacy.create" />
          </span>
        )
      }
    >
      <Field label={t('admin.privacy.state')} className="max-w-56">
        {(p) => (
          <Select {...p} value={state} onChange={(e) => setState(e.target.value as 'open' | 'closed')}>
            <option value="open">{t('admin.privacy.states.open')}</option>
            <option value="closed">{t('admin.privacy.states.closed')}</option>
          </Select>
        )}
      </Field>
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.length ? (
        <EmptyState
          title={t(state === 'open' ? 'admin.privacy.emptyOpen' : 'admin.privacy.emptyClosed')}
          description={t('admin.privacy.emptyLead')}
        />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="font-medium text-pine hover:underline"
                  onClick={() => setOpenId(r.id)}
                >
                  {r.requesterName}
                </button>
                <p className="truncate text-sm text-ink-3">
                  {[
                    r.requesterEmail,
                    t(`admin.privacy.kinds.${r.kind}`),
                    t(`admin.privacy.channels.${r.channel}`),
                    formatDate(r.receivedAt),
                  ].join(' · ')}
                </p>
              </div>
              {r.daysLeft !== null &&
                (r.overdue ? (
                  <Badge tone="rose">{t('admin.privacy.overdue')}</Badge>
                ) : (
                  <span className={`text-sm ${r.daysLeft <= 7 ? 'text-amber' : 'text-ink-3'}`}>
                    {t('admin.privacy.daysLeft', { count: r.daysLeft })}
                  </span>
                ))}
              <Badge tone={statusTone[r.status]}>{t(`admin.privacy.statuses.${r.status}`)}</Badge>
            </li>
          ))}
        </ul>
      )}
      {openId && <PrivacyDialog id={openId} onClose={() => setOpenId(null)} />}
      {creating && <CreateDialog onClose={() => setCreating(false)} />}
    </PageFrame>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    requesterName: '',
    requesterEmail: '',
    kind: 'info' as (typeof KINDS)[number],
    channel: 'email' as (typeof CHANNELS)[number],
    receivedAt: today,
    storeId: '',
    details: '',
  });
  const save = useApiMutation(
    () =>
      api.post('/v1/admin/privacy/requests', {
        requesterName: f.requesterName,
        requesterEmail: f.requesterEmail,
        kind: f.kind,
        channel: f.channel,
        receivedAt: f.receivedAt === today ? undefined : `${f.receivedAt}T12:00:00+03:00`,
        storeId: f.storeId || undefined,
        details: f.details.trim() || undefined,
      }),
    { invalidate: [['privacy']], success: t('admin.privacy.created'), onSuccess: onClose },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.privacy.create')}
      description={t('admin.privacy.createLead')}
      closeLabel={t('common.close')}
      footer={
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
          {t('admin.privacy.save')}
        </Button>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.privacy.requesterName')} error={fieldError(save.error, 'requesterName')}>
          {(p) => (
            <Input
              {...p}
              value={f.requesterName}
              onChange={(e) => setF({ ...f, requesterName: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.privacy.requesterEmail')}
          help={<HelpTip k="privacy.requester_email" />}
          error={fieldError(save.error, 'requesterEmail')}
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              value={f.requesterEmail}
              onChange={(e) => setF({ ...f, requesterEmail: e.target.value })}
            />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('admin.privacy.kind')}>
            {(p) => (
              <Select
                {...p}
                value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value as (typeof KINDS)[number] })}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.privacy.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.privacy.channel')}>
            {(p) => (
              <Select
                {...p}
                value={f.channel}
                onChange={(e) => setF({ ...f, channel: e.target.value as (typeof CHANNELS)[number] })}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {t(`admin.privacy.channels.${c}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.privacy.receivedAt')}
            help={<HelpTip k="privacy.received_at" />}
            error={fieldError(save.error, 'receivedAt')}
          >
            {(p) => (
              <Input
                {...p}
                type="date"
                max={today}
                value={f.receivedAt}
                onChange={(e) => setF({ ...f, receivedAt: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.privacy.store')}>
            {(p) => (
              <Select {...p} value={f.storeId} onChange={(e) => setF({ ...f, storeId: e.target.value })}>
                <option value="">—</option>
                {(stores.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field label={t('admin.privacy.details')}>
          {(p) => (
            <Textarea
              {...p}
              className="min-h-20"
              value={f.details}
              onChange={(e) => setF({ ...f, details: e.target.value })}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function PrivacyDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['privacy', 'detail', id],
    queryFn: () => api.get<PrivacyRequestDetail>(`/v1/admin/privacy/requests/${id}`),
  });
  const [note, setNote] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [stage, setStage] = useState<'view' | 'confirm'>('view');
  const [downloading, setDownloading] = useState(false);
  const d = q.data;
  const invalidate = [['privacy']];
  const status = useApiMutation(
    (to: 'in_progress' | 'completed' | 'rejected') =>
      api.post(`/v1/admin/privacy/requests/${id}/status`, {
        status: to,
        note: note.trim() || undefined,
        version: d?.version,
      }),
    { invalidate, success: t('common.saved'), onSuccess: () => setNote('') },
  );
  const anonymize = useApiMutation(
    () =>
      api.post(
        `/v1/admin/privacy/requests/${id}/anonymize`,
        { confirmEmail, note: note.trim() || undefined },
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      invalidate: [...invalidate, ['customers']],
      success: t('admin.privacy.anonymized'),
      onSuccess: () => {
        setStage('view');
        setConfirmEmail('');
      },
    },
  );
  const download = async () => {
    setDownloading(true);
    try {
      const { blob, fileName } = await api.blob(`/v1/admin/privacy/requests/${id}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'kisisel-veriler.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('admin.privacy.exported'));
      void q.refetch();
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setDownloading(false);
    }
  };
  const open = d && (d.status === 'received' || d.status === 'in_progress');
  const isErasure = d?.kind === 'erasure';
  const error = status.error ?? anonymize.error;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={d ? `${d.requesterName} · ${t(`admin.privacy.kinds.${d.kind}`)}` : '…'}
      closeLabel={t('common.close')}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-5">
          <KeyValue
            items={[
              { label: t('admin.privacy.requesterEmail'), value: d.requesterEmail },
              { label: t('admin.privacy.channel'), value: t(`admin.privacy.channels.${d.channel}`) },
              { label: t('admin.privacy.receivedAt'), value: formatDate(d.receivedAt) },
              {
                label: t('admin.privacy.dueAt'),
                value: d.overdue
                  ? `${formatDate(d.dueAt)} — ${t('admin.privacy.overdue')}`
                  : formatDate(d.dueAt),
              },
              { label: t('admin.privacy.statusLabel'), value: t(`admin.privacy.statuses.${d.status}`) },
              {
                label: t('admin.privacy.customer'),
                value: d.customer ? (
                  <Link
                    to="/musteriler/$id"
                    params={{ id: d.customer.id }}
                    className="text-pine hover:underline"
                  >
                    {d.customer.name}
                  </Link>
                ) : (
                  t('admin.privacy.noCustomer')
                ),
              },
              { label: t('admin.privacy.details'), value: d.details ?? '—' },
              ...(d.resolution ? [{ label: t('admin.privacy.resolution'), value: d.resolution }] : []),
              ...(d.exportedAt
                ? [{ label: t('admin.privacy.exportedAt'), value: formatDate(d.exportedAt, true) }]
                : []),
            ]}
          />

          <div className="rounded-md border border-line p-3 text-sm">
            <p className="mb-1 font-medium">{t('admin.privacy.recordsTitle')}</p>
            <p className="text-ink-2">
              {t('admin.privacy.records', {
                orders: d.counts.orders,
                addresses: d.counts.addresses,
                favorites: d.counts.favorites,
                questions: d.counts.questions,
                companies: d.counts.dealerCompanies,
                invoices: d.counts.invoices,
                acceptances: d.counts.legalAcceptances,
                messages: d.counts.contactMessages,
                tickets: d.counts.supportTickets,
                chats: d.counts.liveChats,
                newsletter: d.counts.newsletter,
                campaigns: d.counts.campaignEmails,
                automations: d.counts.automationEmails,
                stockAlerts: d.counts.stockAlerts,
                smsMessages: d.counts.campaignMessages,
                loyaltyPoints: d.counts.loyaltyPoints,
              })}
            </p>
          </div>

          {can('privacy.data.export') && (
            <span className="flex items-center gap-1">
              <Button loading={downloading} onClick={() => void download()}>
                <Download className="h-4 w-4" aria-hidden />
                {t('admin.privacy.export')}
              </Button>
              <HelpTip k="privacy.export" />
            </span>
          )}

          {isErasure && open && (
            <div className="space-y-2 rounded-md border border-line p-3 text-sm">
              <p className="font-medium">{t('admin.privacy.erasureTitle')}</p>
              <ul className="list-disc space-y-1 ps-5 text-ink-2">
                <li>{t('admin.privacy.erasureDeletes')}</li>
                <li>{t('admin.privacy.erasureKeeps')}</li>
              </ul>
              {d.erasureBlockers.length > 0 && (
                <ul
                  role="alert"
                  className="space-y-1 rounded-sm border border-rose/30 bg-rose-tint px-3 py-2 text-ink"
                >
                  {d.erasureBlockers.map((b) => (
                    <li key={b.code}>{t(`errors.${b.code}`, b.params ?? {})}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {open && can('privacy.request.manage') && (
            <div className="space-y-3 border-t border-line pt-4">
              <Field
                label={t(isErasure ? 'admin.privacy.noteErasure' : 'admin.privacy.note')}
                description={t('admin.privacy.noteHint')}
                help={<HelpTip k="privacy.note" />}
                error={fieldError(status.error, 'note')}
              >
                {(p) => (
                  <Textarea
                    {...p}
                    className="min-h-20"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>
              {stage === 'confirm' && (
                <Field
                  label={t('admin.privacy.confirmEmail', { email: d.requesterEmail })}
                  help={<HelpTip k="privacy.confirm_email" />}
                  error={fieldError(anonymize.error, 'confirmEmail')}
                >
                  {(p) => (
                    <Input
                      {...p}
                      autoComplete="off"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                    />
                  )}
                </Field>
              )}
              {error && (
                <p role="alert" className="text-sm text-rose">
                  {error.errors[0]?.message ?? error.title}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {d.status === 'received' && (
                  <span className="flex items-center gap-1">
                    <Button loading={status.isPending} onClick={() => status.mutate('in_progress')}>
                      {t('admin.privacy.startReview')}
                    </Button>
                    <HelpTip k="privacy.start_review" />
                  </span>
                )}
                {!isErasure && (
                  <span className="flex items-center gap-1">
                    <Button
                      variant="primary"
                      loading={status.isPending}
                      onClick={() => status.mutate('completed')}
                    >
                      {t('admin.privacy.complete')}
                    </Button>
                    <HelpTip k="privacy.complete" />
                  </span>
                )}
                {isErasure && can('privacy.data.erase') && stage === 'view' && (
                  <span className="flex items-center gap-1">
                    <Button
                      variant="danger"
                      disabled={d.erasureBlockers.length > 0}
                      onClick={() => setStage('confirm')}
                    >
                      {t('admin.privacy.anonymize')}
                    </Button>
                    <HelpTip k="privacy.anonymize" />
                  </span>
                )}
                {isErasure && stage === 'confirm' && (
                  <>
                    <Button
                      variant="danger"
                      loading={anonymize.isPending}
                      disabled={confirmEmail.trim().toLowerCase() !== d.requesterEmail.toLowerCase()}
                      onClick={() => anonymize.mutate(undefined)}
                    >
                      {t('admin.privacy.anonymizeConfirm')}
                    </Button>
                    <Button onClick={() => setStage('view')}>{t('common.cancel')}</Button>
                  </>
                )}
                <span className="flex items-center gap-1">
                  <Button loading={status.isPending} onClick={() => status.mutate('rejected')}>
                    {t('admin.privacy.reject')}
                  </Button>
                  <HelpTip k="privacy.reject" />
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
